import { FC, useRef, useState } from 'react';
import UppyUploader, { AssetsType } from './uppy-uploader';
import MediaElement from '../media-element';
import { Media } from '@/__generated__/graphql';
import { Cross2Icon } from '@radix-ui/react-icons';
import { useToast } from '@/components/shadcn/use-toast';
import UppyDropzone from './uppy-dropzone';

type UppyMediaManagerProps = {
  media?: Media[];
  isDisabled?: boolean;
  isDropzone?: boolean;
  assetsType?: AssetsType;
  onUpload: (
    fileId: string,
    previewUrl: string,
    media?: Media[]
  ) => Promise<void>;
  onDelete?: (file: string, media?: Media[]) => Promise<void>;
  onEditDesign?: (id: string) => void;
};

const UppyMediaManager: FC<UppyMediaManagerProps> = ({
  media,
  isDropzone,
  isDisabled,
  assetsType,
  onUpload,
  onDelete,
}) => {
  const [isDisabledLocally, setIsDisabledLocally] = useState(false);
  const mediaRef = useRef([] as Media[]);
  mediaRef.current = media || [];
  const { toast } = useToast();

  const onDeleteClicked = async (
    event: React.MouseEvent<HTMLSpanElement, MouseEvent>,
    imageId: string
  ) => {
    setIsDisabledLocally(true);
    onDelete && (await onDelete(imageId, mediaRef.current));
    setIsDisabledLocally(false);
  };

  const onUploadFile = async (fileId: string, url: string) => {
    setIsDisabledLocally(true);
    await onUpload(fileId, url, mediaRef.current);
    setIsDisabledLocally(false);
  };

  const disabled = isDisabled || isDisabledLocally && !isDropzone;

  return (
    <div>
      {(media?.length || 0) > 0 && (
        <div className='flex flex-wrap gap-6'>
          {media?.map((mediaItem) => (
            <div key={mediaItem.id} className='relative'>
              <div
                key={mediaItem.url}
                className='absolute -right-2 -top-2 cursor-pointer rounded-full bg-gray-200 p-1'
                onClick={(e) => !disabled && onDeleteClicked(e, mediaItem?.id)}
              >
                <Cross2Icon />
              </div>
              <MediaElement url={mediaItem.url} />
            </div>
          ))}
        </div>
      )}

      {isDropzone ? (
        <UppyDropzone
          dropzoneText='Upload a file or drag and drop here'
          assetsType={assetsType}
          isDisabled={disabled}
          onUploadFileReady={onUploadFile}
          onUploadingStarted={() => setIsDisabledLocally(true)}
          onCancelUploading={() => setIsDisabledLocally(false)}
          onUploadError={(fileName) => {
            setIsDisabledLocally(false);
            toast({
              description: 'error uploading for: ' + fileName,
              duration: 3000,
              variant: 'destructive',
            });
          }}
        />
      ) : (
        <UppyUploader
          assetsType={assetsType}
          isDisabled={disabled}
          onUploadFileReady={onUploadFile}
          onUploadingStarted={() => setIsDisabledLocally(true)}
          onCancelUploading={() => setIsDisabledLocally(false)}
          onUploadError={(fileName) => {
            setIsDisabledLocally(false);
            toast({
              description: 'error uploading for: ' + fileName,
              duration: 3000,
              variant: 'destructive',
            });
          }}
        />
      )}
    </div>
  );
};

export default UppyMediaManager;
